import React from 'react';
import { Row, Col, Collapse, Tooltip } from 'antd';
import { I18n } from '@aws-amplify/core';

import FormItem from '@/components/ui/FormItem';
import { VideoIcon } from '@/assets/icons/video';
import { InfoBox } from '../../components/InfoBox';
import InputRadio from '@/components/ui/InputRadio';
import InputNumber from '@/components/ui/InputNumber';
import ButtonDefault from '@/components/ui/ButtonDefault';
import { VideoDrawer } from '../../components/VideoDrawer';
import { BorgScaleTable } from '../../components/BorgScaleTable';
import { ThermalComfortImage } from '../../components/ThermalComfortImage';
import { useEvaluationCategoryQuestions, useInfoEvaluationCategories } from './hooks';
import { TableCategoryQuestionsReview } from '../../components/TableCategoryQuestionsReview';
import * as S from './styles';

interface EvaluationCategoryQuestionsProps {
	goToSubStep: (step: number) => void;
}

const { Panel } = Collapse;

export function EvaluationCategoryQuestions({ goToSubStep }: Readonly<EvaluationCategoryQuestionsProps>): JSX.Element {
	const infoEvaluationCategories = useInfoEvaluationCategories({ goToSubStep });
	const {
		category,
		assessment,
		newReview,
		previousReview,
		step2Questions,
		isLoading,
		previousFormValues,
		activeKey,
		isRepetitiveMovements,
		isThermalComfort,
		openVideoDrawer,
		setOpenVideoDrawer,
		handleSaveQuestions,
		handleKeyPress,
		handleOpenVideoDrawer,
		handleOnToggleCollapse
	} = useEvaluationCategoryQuestions({
		goToSubStep
	});

	return (
		<>
			<S.DefaultRowContent gutter={[32, 16]} align="bottom">
				<Col span={24} style={{ marginBottom: '20px' }}>
					<S.DefaultStepHeading>
						{assessment.activity?.name} - {I18n.get(category?.name)}
					</S.DefaultStepHeading>
					<S.FlexSpaceBetween>
						<S.DefaultStepTitle>{I18n.get('Initial evaluation of risk factors')}</S.DefaultStepTitle>
						<ButtonDefault
							type="link"
							style={{ display: 'flex', alignItems: 'self-end' }}
							onClick={handleOpenVideoDrawer}
						>
							<VideoIcon width={30} height={30} />
							{I18n.get('Watch video')}
						</ButtonDefault>
					</S.FlexSpaceBetween>
					<S.DefaultStepSubTitle>{I18n.get('Fill out the following information.')}</S.DefaultStepSubTitle>
				</Col>
				{(newReview || previousReview?.id) && (
					<Col span={24}>
						<S.CustomButtonDefault
							type="default"
							onClick={handleOnToggleCollapse}
							className={activeKey.length ? 'active' : ''}
						>
							{I18n.get('View previous evaluation')}
						</S.CustomButtonDefault>
						<S.CustomCollapse activeKey={activeKey} bordered={false}>
							<Panel key="1" showArrow={false} header={null}>
								<Row gutter={[48, 16]}>
									<Col span={24}>
										<TableCategoryQuestionsReview
											exposure_time={previousFormValues?.exposure_time || 0}
											first_step={previousFormValues?.mandatory_questions}
											second_step={previousFormValues?.conditional_questions}
										/>
									</Col>
								</Row>
							</Panel>
						</S.CustomCollapse>
					</Col>
				)}
				<Col span={24} lg={10}>
					<S.HeadingSmall style={{ marginBottom: '25px' }}>{I18n.get('Exposure time')}</S.HeadingSmall>

					<FormItem
						required
						name={['questions', 'exposure_time']}
						label={
							<div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
								<span>
									{I18n.get('Indicate the time of exposure to the risk factor (in percentage)')}
								</span>
								<Tooltip
									title={I18n.get(
										'This value will be added to the total exposure time to the Risk Factor.'
									)}
								>
									<S.TooltipIcon />
								</Tooltip>
							</div>
						}
						requiredMessage="Please select a response"
					>
						<InputNumber
							min={0}
							max={100}
							formatter={(val) => (val && val !== '' ? `${val}%` : '')}
							parser={(val) => val?.replace(/\D/g, '') || ''}
							onKeyDown={handleKeyPress}
						/>
					</FormItem>
				</Col>
				<S.CustomColBorder span={24}>
					<S.HeadingSmall style={{ marginBottom: '25px' }}>
						{I18n.get('Step')} 1: {I18n.get('Identify if the task of the job position involves')}:
					</S.HeadingSmall>
					{category?.mandatory_questions.map((question, index) => (
						<FormItem
							key={question.key}
							name={['questions', 'mandatory_questions', question.key]}
							label={`${index + 1}. ${I18n.get(question.text)}`}
							requiredMessage="Please select a response"
							required
						>
							<InputRadio
								options={[
									{ label: I18n.get('Yes'), value: 'yes' },
									{ label: I18n.get('No'), value: 'no' }
								]}
								disabled={isLoading}
							/>
						</FormItem>
					))}
				</S.CustomColBorder>
				{step2Questions && (
					<>
						<S.CustomColBorder span={24}>
							<S.HeadingSmall style={{ marginBottom: '25px' }}>
								{I18n.get('Step')} {I18n.get('2')}: {I18n.get('Determine the risk level')}:
							</S.HeadingSmall>
							<Row gutter={[24, 0]}>
								<Col
									xs={24}
									xxl={isRepetitiveMovements || isThermalComfort ? 18 : 24}
									xl={isRepetitiveMovements || isThermalComfort ? 16 : 24}
								>
									{category?.conditional_questions.map((question, index) => (
										<FormItem
											key={question.key}
											name={['questions', 'conditional_questions', question.key]}
											label={`${index + 1}. ${I18n.get(question.text)}`}
											required
										>
											<InputRadio
												options={[
													{ label: I18n.get('Yes'), value: 'yes' },
													{ label: I18n.get('No'), value: 'no' }
												]}
												disabled={isLoading}
											/>
										</FormItem>
									))}
								</Col>
								{isRepetitiveMovements && (
									<Col xs={24} xxl={6} xl={8}>
										<BorgScaleTable />
									</Col>
								)}
							</Row>
						</S.CustomColBorder>
						{isThermalComfort && (
							<Col xs={24}>
								<ThermalComfortImage />
							</Col>
						)}
						<Col span={24} style={{ marginTop: '40px' }}>
							{infoEvaluationCategories.showConditionalTolerableInfo && (
								<InfoBox
									title={I18n.get(
										'The level of risk is tolerable, so no measures are required. However, a preventive evaluation can be conducted on the next page if requested by any of the responsible parties.'
									)}
									type="info"
								/>
							)}
							{infoEvaluationCategories.showConditionalNotTolerableInfo && (
								<InfoBox
									type="warning"
									title={I18n.get(
										'The risk level is not tolerable; therefore, a risk evaluation by a professional knowledgeable in ergonomics is required. Proceed to the next page to conduct the evaluation'
									)}
								/>
							)}
						</Col>
					</>
				)}
				<Col span={24}>
					{infoEvaluationCategories.showMandatoryTolerableInfo && (
						<InfoBox
							type="info"
							title={I18n.get(
								'The level of risk is tolerable, so no measures are required. However, a preventive evaluation can be conducted on the next page if requested by any of the responsible parties.'
							)}
						/>
					)}
				</Col>
			</S.DefaultRowContent>
			<Row gutter={[16, 16]} style={{ padding: '30px 0' }}>
				<Col span={24} style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
					<ButtonDefault type="default" onClick={() => goToSubStep(2)} loading={isLoading}>
						{I18n.get('Back')}
					</ButtonDefault>
					<ButtonDefault type="primary" onClick={handleSaveQuestions} loading={isLoading}>
						{I18n.get('Next')}
					</ButtonDefault>
				</Col>
			</Row>
			<VideoDrawer open={openVideoDrawer} onClose={() => setOpenVideoDrawer(false)} />
		</>
	);
}
